import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Users, ShieldCheck, Clock, AlertTriangle } from "lucide-react";
import { api } from "../../api/client";
import { formatCalendarDate } from "../../lib/calendar-date";
import { getRoleLabel } from "../../lib/roles";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";


type CertStatus = "valid" | "expiring_soon" | "expired" | "not_started";

interface RawCertRow {
  employee_id: string;
  employee_name: string;
  employee_email?: string;
  course_id: string;
  course_title: string;
  certification_id?: string;
  certificate_number?: string;
  issued_at?: string;
  expires_at?: string;
  status: string;
  days_until_expiry?: number;
}

interface CourseCert {
  course_id: string;
  course_title: string;
  status: CertStatus;
  expires_at?: string;
  expired_at?: string;
  days_remaining?: number;
}

interface EmployeeRow {
  user_id: string;
  employee_name: string;
  role: string;
  courses: CourseCert[];
}

interface ComplianceData {
  employees: EmployeeRow[];
  courses: { id: string; title: string }[];
  stats: {
    total_employees: number;
    fully_certified: number;
    expiring_soon: number;
    expired_or_never: number;
  };
}

function mapApiStatus(status: string): CertStatus {
  if (status === "active") return "valid";
  if (status === "expiring_soon") return "expiring_soon";
  if (status === "expired" || status === "never") return "expired";
  return "not_started";
}

function transformFlatRows(rows: RawCertRow[]): ComplianceData {
  const employeeMap = new Map<string, { name: string; certs: Map<string, CourseCert> }>();
  const courseMap = new Map<string, string>();

  for (const row of rows) {
    courseMap.set(row.course_id, row.course_title);

    let emp = employeeMap.get(row.employee_id);
    if (!emp) {
      emp = { name: row.employee_name, certs: new Map() };
      employeeMap.set(row.employee_id, emp);
    }

    const mapped: CertStatus = mapApiStatus(row.status);
    emp.certs.set(row.course_id, {
      course_id: row.course_id,
      course_title: row.course_title,
      status: mapped,
      expires_at: row.expires_at,
      expired_at: mapped === "expired" ? row.expires_at : undefined,
      days_remaining: row.days_until_expiry ?? undefined,
    });
  }

  const courses = Array.from(courseMap.entries()).map(([id, title]) => ({ id, title }));

  const employees: EmployeeRow[] = Array.from(employeeMap.entries()).map(([id, emp]) => ({
    user_id: id,
    employee_name: emp.name,
    role: "",
    courses: Array.from(emp.certs.values()),
  }));

  const totalEmployees = employees.length;
  const courseIds = courses.map((c) => c.id);

  let fullyCertified = 0;
  let expiringSoon = 0;
  let expiredOrNever = 0;

  for (const emp of employees) {
    // `[].every(p)` is `true`, so with no courses configured every employee
    // scored as fully certified. "Fully certified against nothing" is not a
    // compliance fact, and this dashboard is read as one.
    const allActive = courseIds.length > 0 && courseIds.every((cid) => {
      const cert = emp.courses.find((c) => c.course_id === cid);
      return cert?.status === "valid";
    });
    if (allActive) fullyCertified++;

    const hasExpiring = emp.courses.some((c) => c.status === "expiring_soon");
    if (hasExpiring) expiringSoon++;

    const hasExpiredOrNever = emp.courses.some((c) => c.status === "expired") ||
      courseIds.some((cid) => !emp.courses.find((c) => c.course_id === cid));
    if (hasExpiredOrNever) expiredOrNever++;
  }

  return {
    employees,
    courses,
    stats: {
      total_employees: totalEmployees,
      fully_certified: fullyCertified,
      expiring_soon: expiringSoon,
      expired_or_never: expiredOrNever,
    },
  };
}

/* Saturated signal fills with dark ink — the badge idiom, which holds in both
 * themes — in place of pale washes with a bright hue as ink. */
const BADGE = "text-[10px] px-2 py-0.5 rounded-full font-semibold border border-black/20 text-fi-signal-ink";

const statusBadge = (cert: CourseCert | undefined) => {
  if (!cert || cert.status === "not_started") {
    return <span className={`${BADGE} bg-fi-signal-slate`}>Not Started</span>;
  }
  if (cert.status === "valid") {
    const expires = formatCalendarDate(cert.expires_at, { month: "short", day: "numeric", year: "numeric" });
    return (
      <span className={`${BADGE} bg-fi-signal-green`}>
        Valid{expires ? ` · ${expires}` : ""}
      </span>
    );
  }
  if (cert.status === "expiring_soon") {
    return (
      <span className={`${BADGE} bg-fi-signal-amber`}>
        Expires Soon{cert.days_remaining != null ? ` · ${cert.days_remaining}d` : ""}
      </span>
    );
  }
  // expired
  const expired = formatCalendarDate(cert.expired_at, { month: "short", day: "numeric" });
  return (
    <span className={`${BADGE} bg-fi-signal-red`}>
      Expired{expired ? ` · ${expired}` : ""}
    </span>
  );
};

/* The shared exhaustive role map, not a hand-rolled underscore swap with a
 * guessed "employee" for an absent role. */
const roleBadge = (role: string) => {
  const label = role ? getRoleLabel(role) : "\u2014";
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold border border-fieldiq-border bg-fieldiq-dark text-fieldiq-text">
      {label}
    </span>
  );
};

const ComplianceDashboard = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | CertStatus>("all");

  /**
   * A COMPLIANCE DASHBOARD IS THE LAST PLACE A FAILURE MAY READ AS A ZERO.
   *
   * With `{ data, isLoading }` only, a refused or dropped request left `data`
   * undefined, `stats` fell to the zeroed literal below, and the four cards
   * read **Total Employees 0 / Fully Certified 0 / Expiring Soon 0 / Expired
   * or Never Certified 0** over "No employees found." An admin checking
   * whether anyone is out of ticket is shown a clean board.
   *
   * The unrecognised-shape branch is the same bug by another route: a payload
   * this screen cannot read is not a company with no employees, so it throws
   * and lands in the same place as a network failure instead of manufacturing
   * an all-zero answer.
   */
  const complianceQuery = useQuery<ComplianceData>({
    queryKey: ["training-certifications"],
    queryFn: async () => {
      const r: any = await api.safety.trainingCertifications();
      const raw = r.data?.data ?? r.data;
      if (Array.isArray(raw)) return transformFlatRows(raw as RawCertRow[]);
      if (raw?.employees) return raw as ComplianceData;
      throw new Error('GET training certifications returned a shape this screen cannot read');
    },
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data, isLoading } = complianceQuery;
  // A query waiting to retry reports `error: null`. See `queryLoadFailure`.
  const loadFailure = queryLoadFailure(complianceQuery);

  const employees = data?.employees ?? [];
  const courses = data?.courses ?? [];
  /* No zero fallback: while the read is in flight or after it fails, each
   * figure is absent and renders an em dash, never a placeholder 0. */
  const stats = data?.stats;

  const filtered = useMemo(() => {
    return employees.filter((emp) => {
      // name search
      if (search && !emp.employee_name.toLowerCase().includes(search.toLowerCase())) return false;
      // status filter
      if (statusFilter !== "all") {
        const hasMatch = emp.courses.some((c) => c.status === statusFilter);
        const hasNotStarted = statusFilter === "not_started" && courses.some((course) => !emp.courses.find((c) => c.course_id === course.id));
        if (!hasMatch && !hasNotStarted) return false;
      }
      return true;
    });
  }, [employees, courses, search, statusFilter]);

  /* The hue sits on a saturated disc behind the icon, with dark ink on it; the
   * figure is theme ink. A bright hue as the figure's own ink read under 2:1
   * on a white card. */
  const statCards = [
    { label: "Total Employees", value: stats?.total_employees, icon: Users, fill: "bg-fi-signal-blue" },
    { label: "Fully Certified", value: stats?.fully_certified, icon: ShieldCheck, fill: "bg-fi-signal-green" },
    { label: "Expiring Soon", value: stats?.expiring_soon, icon: Clock, fill: "bg-fi-signal-amber" },
    { label: "Expired / Never Certified", value: stats?.expired_or_never, icon: AlertTriangle, fill: "bg-fi-signal-red" },
  ];

  /* Under `SafetyLayout`, which owns the band, the <h1> and the section strip.
   * The one action this page had in its header sits at the head of the body. */
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={() => navigate("/safety/training/courses")}
          className="fi-action fi-action--standard fi-action--primary"
        >
          Manage Courses
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
            <div className="flex items-center gap-3">
              <span className={`flex size-9 flex-shrink-0 items-center justify-center rounded-full ${card.fill}`}>
                <card.icon className="w-4 h-4 text-fi-signal-ink" aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs text-fieldiq-muted">{card.label}</p>
                {/* A dash, not a zero. See the query's docblock. */}
                <p className="text-2xl font-bold tabular-nums text-fieldiq-text">
                  {displayValue(card.value)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 pl-9 pr-3 bg-background border border-border rounded-lg text-foreground text-sm placeholder-muted-foreground focus:outline-none focus:border-primary/50"
          />
        </div>
        <select
          value={courseFilter}
          onChange={(e) => setCourseFilter(e.target.value)}
          className="h-9 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
        >
          <option value="">All Courses</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="h-9 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
        >
          <option value="all">All Statuses</option>
          <option value="valid">Valid</option>
          <option value="expiring_soon">Expiring Soon</option>
          <option value="expired">Expired</option>
          <option value="not_started">Not Started</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading compliance data...</div>
      ) : loadFailure ? (
        <LoadFailureNotice
          error={queryFailureError(complianceQuery)}
          kind={loadFailure}
          subject="training compliance"
          allowed="Admins, office staff and safety roles"
        />
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">No employees found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                <th className="text-left py-2 px-3">Employee</th>
                {(courseFilter ? courses.filter((c) => c.id === courseFilter) : courses).map((course) => (
                  <th key={course.id} className="text-left py-2 px-3">{course.title}</th>
                ))}
              </tr>
            </thead>
            {/* NO "SEND REMINDER" COLUMN, AND THAT IS DELIBERATE. The button that
              * lived here toasted "Reminder sent to <name>" and called nothing —
              * someone believed a reminder had gone out when none had.
              *
              * The one endpoint nearby, POST /jobs/cert-reminder, cannot back a
              * per-employee reminder as it stands: it takes the recipient's email
              * AND phone from the request body (this payload has no phone, so the
              * SMS half would queue with no recipient and still answer 202), it
              * speaks of a single certification "expiring in N days" (wrong for
              * an expired row, and it throws building a date for a
              * never-certified one), and its role gate is admin plus a role that
              * does not exist, while this page is open to office staff.
              * A reminder comes back when the API resolves the recipient from an
              * employee id and a course. */}
            <tbody>
              {filtered.map((emp) => {
                const visibleCourses = courseFilter ? courses.filter((c) => c.id === courseFilter) : courses;
                return (
                  <tr key={emp.user_id} className="border-t border-border hover:bg-secondary/30 transition-colors">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{emp.employee_name}</span>
                        {roleBadge(emp.role)}
                      </div>
                    </td>
                    {visibleCourses.map((course) => {
                      const cert = emp.courses.find((c) => c.course_id === course.id);
                      return <td key={course.id} className="py-3 px-3">{statusBadge(cert)}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ComplianceDashboard;
